/**
 * toast.helpers.tsx
 *
 * Centralised Sonner toast utilities for custom-designed notifications.
 * Provides shared styles, icons, and factory functions so any feature can
 * fire consistently styled toasts without duplicating configuration.
 *
 * Usage:
 *   import { showLoadingToast, showSuccessToast, showErrorToast } from '@/helpers/toast.helpers';
 *
 *   const id = showLoadingToast('Submitting…');
 *   showSuccessToast('Done!', 'Your changes were saved.', id);
 *
 *   // With an action button:
 *   showErrorToast('Download failed', undefined, undefined, {
 *     label: 'Try again',
 *     onClick: () => retryDownload(),
 *   });
 */

import React from 'react';
import { toast } from 'sonner';
import { Check, Loader, X } from 'lucide-react';

// ---------------------------------------------------------------------------
// Toast duration constants (in milliseconds)
// ---------------------------------------------------------------------------

export const TOAST_DURATIONS = {
  DEFAULT: 5000,
  LOADING: Infinity,
} as const;

// ---------------------------------------------------------------------------
// Shared base style – only the border differs between variants
// ---------------------------------------------------------------------------

const BASE_TOAST_STYLE = {
  display: 'flex',
  width: '420px',
  maxWidth: 'calc(100vw - 32px)',
  padding: 'var(--openui-space-m)',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  backgroundColor: 'var(--dash-surface, var(--popover))',
  borderRadius: '13px',
  boxShadow: 'var(--dash-shadow, var(--openui-shadow-xl))',
  color: 'var(--dash-ink, var(--text-high-emphasis))',
  gap: 'var(--openui-space-m)',
} as const;

// Neutral 1px hairline all round; semantic meaning is a 3px left rail only
// (matches the [data-type=…] rules in index.css). Inline border is set here
// so it wins over the CSS rail — the variant styles bake the rail back in.
const HAIRLINE = '1px solid var(--dash-hairline, var(--border))';

export const TOAST_STYLES = {
  default: { ...BASE_TOAST_STYLE, border: HAIRLINE },
  loading: {
    ...BASE_TOAST_STYLE,
    border: HAIRLINE,
    borderLeft: '3px solid var(--dash-accent, var(--primary-500))',
  },
  success: {
    ...BASE_TOAST_STYLE,
    border: HAIRLINE,
    borderLeft: '3px solid var(--dash-pos, var(--semantic-success-600))',
  },
  error: {
    ...BASE_TOAST_STYLE,
    border: HAIRLINE,
    borderLeft: '3px solid var(--dash-neg, var(--semantic-error-600))',
  },
} as const;

// ---------------------------------------------------------------------------
// Shared classNames
// ---------------------------------------------------------------------------

export const TOAST_CLASS_NAMES = {
  toast:        'items-start [&_[data-title]]:font-medium [&_[data-title]]:text-[14px] [&_[data-title]]:leading-[21px] [&_[data-description]]:mt-[2px] [&_[data-description]]:font-normal [&_[data-description]]:text-[14px] [&_[data-description]]:leading-[21px]',
} as const;

// For toasts that pass their own JSX body (`showToast(<Custom />, …)`): sonner
// wraps that body in a `[data-content]` flex item that shrink-wraps to the
// text, so a custom body's own close/CTA controls end up mid-card instead of
// at the right edge. `content: w-full` stretches the wrapper to the card. Not
// folded into TOAST_CLASS_NAMES: the standard toasts render sonner's own
// action button beside `[data-content]`, and a full-width content there would
// push that button out of the card.
export const TOAST_CUSTOM_CLASS_NAMES = {
  ...TOAST_CLASS_NAMES,
  content: 'w-full',
} as const;

// className applied to sonner's auto-rendered action button when a custom
// CTA (e.g. "Try again") is passed to showErrorToast. `!self-center` keeps
// the button vertically centred against the icon + text block instead of
// being pinned to the top by the toast's `items-start` alignment. The `!`
// prefixes override sonner's own default styles which would otherwise win
// on specificity.
const TOAST_ACTION_BUTTON_CLASS =
  '!h-auto !self-center !rounded !border !border-[var(--dash-accent,var(--primary-500))] !bg-transparent !px-3 !py-1.5 !text-sm !font-medium !text-[var(--dash-accent,var(--primary-500))] !shadow-none hover:!bg-[var(--dash-accent-soft,var(--primary-50))] !transition-colors';

// ---------------------------------------------------------------------------
// Shared icons
// ---------------------------------------------------------------------------

export const ToastIcons = {
  loading: (
    <div className="flex h-6 w-6 items-center justify-center">
      <Loader
        className="h-6 w-6 animate-spin"
        style={{ color: 'var(--dash-accent, var(--primary-500))' }}
        strokeWidth={1.75}
      />
    </div>
  ),

  success: (
    <div
      className="flex h-6 w-6 items-center justify-center rounded-full"
      style={{ backgroundColor: 'color-mix(in srgb, var(--dash-pos, var(--semantic-success-600)) 14%, transparent)' }}
    >
      <div
        className="flex h-4 w-4 items-center justify-center rounded-full"
        style={{ backgroundColor: 'var(--dash-pos, var(--semantic-success-600))' }}
      >
        <Check className="h-3.5 w-3.5 text-[var(--structural-white)]" strokeWidth={2.5} />
      </div>
    </div>
  ),

  error: (
    <div
      className="flex h-6 w-6 items-center justify-center rounded-full"
      style={{ backgroundColor: 'color-mix(in srgb, var(--dash-neg, var(--semantic-error-600)) 14%, transparent)' }}
    >
      <div
        className="flex h-6 w-6 items-center justify-center rounded-full"
        style={{ backgroundColor: 'var(--dash-neg, var(--semantic-error-600))' }}
      >
        <X className="h-3.5 w-3.5 text-[var(--structural-white)]" strokeWidth={2.5} />
      </div>
    </div>
  ),
} as const;

// ---------------------------------------------------------------------------
// Dismiss-button action factory
// ---------------------------------------------------------------------------

function makeDismissAction(toastId: string | number) {
  return {
    label: <X className="h-5 w-5" style={{ color: 'var(--dash-muted, var(--icon-grey-1))' }} />,
    onClick: () => toast.dismiss(toastId),
  };
}

// ---------------------------------------------------------------------------
// Toast action type (for the "Try again" / custom CTA pattern)
// ---------------------------------------------------------------------------

export interface ToastAction {
  label: string;
  onClick: () => void;
}

// ---------------------------------------------------------------------------
// Public factory functions
// ---------------------------------------------------------------------------

/**
 * Shows a loading-state toast and returns its id so it can be updated later.
 *
 * @param title - Toast heading
 * @param description - Optional subtitle
 * @param icon - Optional custom icon node; defaults to the spinning Loader
 */
export function showLoadingToast(
  title: string,
  description?: string,
  icon?: React.ReactNode,
): string | number {
  return toast(title, {
    description,
    duration: TOAST_DURATIONS.LOADING,
    icon: icon ?? ToastIcons.loading,
    style: TOAST_STYLES.loading,
    classNames: TOAST_CLASS_NAMES,
  });
}

/**
 * Shows (or updates) a success toast.
 *
 * @param title - Toast heading
 * @param description - Optional subtitle
 * @param toastId - If provided, updates an existing toast in-place
 * @param icon - Optional custom icon node; defaults to the green Check circle
 * @param duration - Auto-dismiss delay in ms (default TOAST_DURATIONS.DEFAULT)
 */
export function showSuccessToast(
  title: string,
  description?: string,
  toastId?: string | number,
  icon?: React.ReactNode,
  duration: number = TOAST_DURATIONS.DEFAULT,
): string | number {
  const id = toastId ?? toast.success(title);
  toast.success(title, {
    id,
    description,
    duration,
    icon: icon ?? ToastIcons.success,
    action: makeDismissAction(id),
    style: TOAST_STYLES.success,
    classNames: TOAST_CLASS_NAMES,
  });
  return id;
}

/**
 * Shows an error toast with an optional custom CTA (e.g. "Try again").
 *
 * @param title - Toast heading
 * @param description - Optional subtitle
 * @param toastId - If provided, updates an existing toast in-place
 * @param action - Custom CTA to replace the default dismiss button
 * @param icon - Optional custom icon node; defaults to the red X circle
 * @param duration - Auto-dismiss delay in ms (default TOAST_DURATIONS.DEFAULT)
 */
export function showErrorToast(
  title: string,
  description?: string,
  toastId?: string | number,
  action?: ToastAction,
  icon?: React.ReactNode,
  duration: number = TOAST_DURATIONS.DEFAULT,
): string | number {
  const tempId = toastId ?? toast.error(title);

  // When a custom CTA is passed, hand sonner a plain { label, onClick }
  // and let it render its own <button>. Wrapping the label in a custom
  // <button> creates invalid button-in-button nesting and breaks the
  // toast's vertical alignment — the styling is applied via the
  // actionButton classNames slot below instead.
  const resolvedAction = action
    ? { label: action.label, onClick: action.onClick }
    : makeDismissAction(tempId);

  toast.error(title, {
    id: tempId,
    description,
    duration,
    icon: icon ?? ToastIcons.error,
    action: resolvedAction,
    style: TOAST_STYLES.error,
    classNames: action
      ? { ...TOAST_CLASS_NAMES, actionButton: TOAST_ACTION_BUTTON_CLASS }
      : TOAST_CLASS_NAMES,
  });
  return tempId;
}

/**
 * Fires a loading toast immediately and returns its id.
 * Call showSuccessToast(title, description, toastId) when the async work completes
 * to swap the same toast in-place — no artificial timer needed.
 *
 * @param title - Loading state heading
 * @param description - Optional subtitle
 * @param icon - Optional custom icon; defaults to the spinning Loader
 */
export function showLoadingThenSuccessToast(
  title: string,
  description?: string,
  icon?: React.ReactNode,
): { toastId: string | number; cancel: () => void } {
  const toastId = showLoadingToast(title, description, icon);
  return {
    toastId,
    cancel: () => toast.dismiss(toastId),
  };
}

// ---------------------------------------------------------------------------
// Low-level passthroughs
// ---------------------------------------------------------------------------
//
// These forward directly to sonner for the two shapes the styled factories
// above don't cover: a fully custom toast body (a bespoke React node, e.g.
// `InviteUserToast` / `TrialStartedToast`) and dismissal. They exist so every
// feature can route through `@/helpers/toast.helpers` and no module has to
// import `sonner` directly (enforced by the `no-restricted-imports` lint rule).

type ToastContent = Parameters<typeof toast>[0];
type ToastOptions = Parameters<typeof toast>[1];

/**
 * Renders a toast with a fully custom body. Use the styled `show*Toast`
 * factories for standard title/description toasts; reach for this only when a
 * feature needs its own toast component/layout.
 */
export function showToast(content: ToastContent, options?: ToastOptions): string | number {
  return toast(content, options);
}

/** Dismisses a toast by id, or all toasts when called with no id. */
export function dismissToast(toastId?: string | number): void {
  toast.dismiss(toastId);
}
